import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag,
  CalendarDays,
  ChevronDown,
  Printer,
  Trash2,
  Search,
} from "lucide-react";
import {
  useAdminAuth,
  AdminLogin,
  AdminLoading,
  useNewItemAlert,
  ConfirmDelete,
} from "./AdminAuth";
import AdminBar from "./AdminBar";
import { OrderEditModal } from "./OrderEditModal";
import { getSeenIds, markSeenIds } from "./siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG = {
  orders: {
    title: "Commandes",
    icon: ShoppingBag,
    dateOf: (x) => x.date || x.created_at,
    otherTo: "/admin/reservas",
    otherLabel: "Voir les réservations",
  },
  reservations: {
    title: "Réservations",
    icon: CalendarDays,
    dateOf: (x) => x.date,
    otherTo: "/admin/encomendas",
    otherLabel: "Voir les commandes",
  },
};

const fmtDay = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const fmtMonth = (ym) =>
  new Date(`${ym}-15T12:00:00`).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

export const printDay = (tipo, day, list) => {
  const isOrders = tipo === "orders";
  const title = isOrders ? "Commandes" : "Réservations";
  const rows = list
    .map((x) => {
      if (isOrders) {
        const items = x.items
          .map(
            (it) =>
              `<li>${it.quantity}× ${it.name} — €${(it.price * it.quantity).toFixed(2)}</li>`
          )
          .join("");
        const time = new Date(x.created_at).toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        });
        return `<div class="card"><div class="top"><strong>${x.name}</strong><span>${time}</span></div><div>${x.phone}</div><ul>${items}</ul><div class="total">Total : €${x.total.toFixed(2)}</div>${x.notes ? `<div class="notes">Notes : ${x.notes}</div>` : ""}</div>`;
      }
      return `<div class="card"><div class="top"><strong>${x.time} — ${x.name}</strong><span>${x.table_numero ? `Table ${x.table_numero} · ` : ""}${x.guests} pers.</span></div><div>${x.phone}${x.email ? ` · ${x.email}` : ""}</div></div>`;
    })
    .join("");
  const w = window.open("", "_blank", "width=800,height=600");
  if (!w) return;
  w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${title} — ${fmtDay(day)}</title><style>body{font-family:Georgia,serif;color:#102C1F;padding:32px;max-width:640px;margin:auto}h1{font-style:italic;margin-bottom:4px}h2{font-weight:normal;text-transform:uppercase;letter-spacing:2px;font-size:13px;border-bottom:1px solid #102C1F;padding-bottom:8px}.card{border:1px solid #bbb;padding:12px 16px;margin-bottom:12px;border-radius:4px}.top{display:flex;justify-content:space-between;font-size:16px}ul{margin:8px 0;padding-left:18px}.total{font-weight:bold;margin-top:6px}.notes{font-size:12px;color:#555}</style></head><body><h1>J'aime la vie</h1><h2>${title} — ${fmtDay(day)}</h2>${rows}</body></html>`);
  w.document.close();
  w.focus();
  w.print();
};

export const statusPill = (active, kind) =>
  `rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
    active
      ? kind === "done"
        ? "border-[#102C1F] bg-[#102C1F] text-[#F4F1EB]"
        : kind === "cancel"
          ? "border-red-700 bg-red-700 text-white"
          : "border-amber-700 bg-amber-700 text-white"
      : "border-[#102C1F]/25 text-[#102C1F]/60 hover:border-[#102C1F]"
  }`;

export const DeleteBtn = ({ testid, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-label="Supprimer"
    className="rounded-full p-1.5 text-[#102C1F]/40 transition-colors duration-300 hover:bg-red-50 hover:text-red-600"
  >
    <Trash2 size={15} />
  </button>
);

export const OrderCard = ({ o, i, onStatus, onDelete, onEdit }) => (
  <article
    data-testid={`order-row-${i}`}
    className={`rounded-sm border p-5 transition-colors duration-300 ${
      o.printed
        ? "border-[#102C1F]/15 bg-[#EDE8DE]"
        : "border-[#D4AF37]/50 bg-[#F9EFD2]"
    }`}
  >
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="font-medium">{o.name}</h3>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#102C1F]/55">
          {new Date(o.created_at).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        {onEdit && (
          <button
            type="button"
            data-testid={`edit-order-${i}`}
            onClick={() => onEdit(o)}
            className="rounded-full border border-[#102C1F]/40 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-[#102C1F] transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            Modifier
          </button>
        )}
        {onDelete && (
          <DeleteBtn
            testid={`delete-order-${i}`}
            onClick={() => onDelete(o.id, o.name)}
          />
        )}
      </div>
    </div>
    <p className="mt-1 text-sm text-[#102C1F]/70">{o.phone}</p>
    {o.date && (
      <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[#102C1F]/55">
        À emporter le{" "}
        {new Date(`${o.date}T12:00:00`).toLocaleDateString("fr-FR", {
          weekday: "short",
          day: "numeric",
          month: "short",
        })}
        {o.time && ` à ${o.time.replace(":", "h")}`}
      </p>
    )}
    <ul className="mt-3 space-y-1 border-t border-[#102C1F]/10 pt-3 text-sm">
      {o.items.map((it, j) => (
        <li key={j} className="flex justify-between gap-3">
          <span>
            {it.quantity}× {it.name}
          </span>
          <span className="font-display italic">
            €{(it.price * it.quantity).toFixed(2)}
          </span>
        </li>
      ))}
    </ul>
    <div className="mt-3 flex items-baseline justify-between border-t border-[#102C1F]/10 pt-3">
      <span className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
        Total
      </span>
      <span className="font-display text-lg italic">€{o.total.toFixed(2)}</span>
    </div>
    {o.notes && (
      <p className="mt-2 text-xs text-[#102C1F]/60">Notes : {o.notes}</p>
    )}
    {onStatus && (
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#102C1F]/10 pt-3">
        <span className="mr-1 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
          Statut
        </span>
        <button
          data-testid={`order-status-prep-${i}`}
          onClick={() => onStatus(o.id, "preparation")}
          className={statusPill(o.status === "preparation", "prep")}
        >
          En préparation
        </button>
        <button
          data-testid={`order-status-done-${i}`}
          onClick={() => onStatus(o.id, "done")}
          className={statusPill(o.status === "done", "done")}
        >
          Remise
        </button>
      </div>
    )}
  </article>
);

const ReservationCard = ({ r, i, onStatus, onDelete }) => (
  <article
    data-testid={`reservation-row-${i}`}
    className="flex flex-wrap items-baseline justify-between gap-3 rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-5"
  >
    <div>
      <h3 className="font-medium">{r.name}</h3>
      <p className="mt-1 text-sm text-[#102C1F]/70">
        {r.phone}
        {r.email ? ` · ${r.email}` : ""}
      </p>
    </div>
    <div className="flex items-center gap-2">
      <div className="text-right">
        <p className="font-display text-lg italic">
          {r.time} · {r.guests} {r.guests === 1 ? "personne" : "personnes"}
          {r.privatisation
            ? " · Restaurant entier"
            : r.table_numero
              ? ` · Table ${r.table_numero}`
              : ""}
        </p>
        <p className="text-xs not-italic text-[#102C1F]/55">
          {new Date(r.created_at).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
      {onDelete && (
        <DeleteBtn
          testid={`delete-reservation-${i}`}
          onClick={() => onDelete(r.id, r.name)}
        />
      )}
    </div>
    {onStatus && (
      <div className="mt-3 flex w-full flex-wrap items-center gap-2 border-t border-[#102C1F]/10 pt-3">
        <span className="mr-1 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
          Statut
        </span>
        <button
          data-testid={`reservation-status-confirmee-${i}`}
          onClick={() => onStatus(r.id, "confirmee")}
          className={statusPill(r.status === "confirmee", "prep")}
        >
          Confirmée
        </button>
        <button
          data-testid={`reservation-status-annulee-${i}`}
          onClick={() => onStatus(r.id, "annulee")}
          className={statusPill(r.status === "annulee", "cancel")}
        >
          Annulée
        </button>
        <button
          data-testid={`reservation-status-terminee-${i}`}
          onClick={() => onStatus(r.id, "terminee")}
          className={statusPill(r.status === "terminee", "done")}
        >
          Terminée
        </button>
      </div>
    )}
  </article>
);

export function DateGroups({ tipo, items, onStatus, onDelete, onPrinted, onEdit }) {
  const [seenIds, setSeenIds] = useState(() => new Set(getSeenIds(tipo)));

  const groups = Object.entries(
    items.reduce((acc, x) => {
      const k = CONFIG[tipo].dateOf(x).slice(0, 10);
      (acc[k] = acc[k] || []).push(x);
      return acc;
    }, {})
  ).sort((a, b) => b[0].localeCompare(a[0]));

  const markGroupSeen = useCallback(
    (list) => {
      markSeenIds(tipo, list.map((x) => x.id));
      setSeenIds(new Set(getSeenIds(tipo)));
    },
    [tipo]
  );

  const firstKey = groups.length ? groups[0][0] : "";
  const firstSig = groups.length ? groups[0][1].map((x) => x.id).join(",") : "";
  useEffect(() => {
    if (groups.length) markGroupSeen(groups[0][1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, firstKey, firstSig]);

  if (groups.length === 0)
    return (
      <p className="text-sm text-[#102C1F]/60">
        Aucun enregistrement pour le moment.
      </p>
    );

  return (
    <div>
      {groups.map(([day, list], gi) => {
        const unseen = list.filter((x) => !seenIds.has(x.id)).length;
        return (
        <details
          key={day}
          id={`day-${day}`}
          className="group mb-8 scroll-mt-24"
          open={gi === 0}
          onToggle={(e) => {
            if (e.currentTarget.open) markGroupSeen(list);
          }}
        >
          <summary
            data-testid={`date-group-${gi}`}
            className="flex cursor-pointer list-none items-center justify-between border-b border-[#102C1F]/15 pb-3 font-display text-2xl italic tracking-tight transition-colors duration-300 hover:text-[#102C1F]/70 [&::-webkit-details-marker]:hidden"
          >
            <span className="relative inline-flex">
              {fmtDay(day)}{" "}
              <span className="text-sm not-italic text-[#102C1F]/50">
                ({list.length})
              </span>
              {unseen > 0 && (
                <span
                  data-testid={`date-group-unseen-${gi}`}
                  className="pointer-events-none absolute -right-3 -top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-600 px-1.5 font-sans text-xs font-bold not-italic text-white shadow-md"
                >
                  {unseen}
                </span>
              )}
            </span>
            <span className="flex items-center gap-3">
              <button
                type="button"
                data-testid={`print-day-${gi}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  printDay(tipo, day, list);
                  if (tipo === "orders" && onPrinted) onPrinted(list);
                }}
                className="flex items-center gap-1.5 rounded-full border border-[#102C1F]/25 px-3 py-1.5 font-sans text-[10px] uppercase not-italic tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
              >
                <Printer size={13} /> Imprimer
              </button>
              <ChevronDown
                size={20}
                className="shrink-0 transition-transform duration-300 group-open:rotate-180"
              />
            </span>
          </summary>
          <div className="mt-5 space-y-4">
            {list.map((x, i) =>
              tipo === "orders" ? (
                <OrderCard
                  key={x.id}
                  o={x}
                  i={`${day}-${i}`}
                  onStatus={onStatus}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              ) : (
                <ReservationCard
                  key={x.id}
                  r={x}
                  i={`${day}-${i}`}
                  onStatus={onStatus}
                  onDelete={onDelete}
                />
              )
            )}
          </div>
        </details>
        );
      })}
    </div>
  );
}

export default function AdminListPage({ tipo }) {
  const cfg = CONFIG[tipo];
  const Icon = cfg.icon;
  const [user, setUser] = useAdminAuth();
  const [items, setItems] = useState([]);

  useNewItemAlert(items.length);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/${tipo}/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setItems((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const markPrinted = async (list) => {
    if (tipo !== "orders") return;
    const ids = list.filter((x) => !x.printed).map((x) => x.id);
    if (!ids.length) return;
    try {
      await axios.post(
        `${API}/orders/mark-printed`,
        { ids },
        { withCredentials: true }
      );
      setItems((prev) =>
        prev.map((x) => (ids.includes(x.id) ? { ...x, printed: true } : x))
      );
    } catch {
      toast.error("Impression enregistrée hors ligne impossible.");
    }
  };

  const [toDelete, setToDelete] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [selectedDate, setSelectedDate] = useState(currentMonth);
  const [search, setSearch] = useState("");
  const [seenTick, setSeenTick] = useState(0);

  const q = search.trim().toLowerCase();
  const shownItems = items.filter((x) => {
    if (selectedDate && cfg.dateOf(x).slice(0, 7) !== selectedDate)
      return false;
    if (!q) return true;
    const day = cfg.dateOf(x).slice(0, 10);
    return (
      x.name.toLowerCase().includes(q) ||
      (x.phone || "").toLowerCase().includes(q) ||
      day.includes(q) ||
      fmtDay(day).toLowerCase().includes(q)
    );
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seenSet = new Set(getSeenIds(tipo));
  const unseenByMonth = items.reduce((acc, x) => {
    if (seenSet.has(x.id)) return acc;
    const m = cfg.dateOf(x).slice(0, 7);
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const totalUnseen = Object.values(unseenByMonth).reduce((s, n) => s + n, 0);
  void seenTick;

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/${toDelete.tipo}/${toDelete.id}`, {
        withCredentials: true,
      });
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/${tipo}`, {
      withCredentials: true,
    });
    setItems(data);
  }, [tipo]);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
    const id = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(id);
  }, [user, load]);

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title={`${cfg.title} par date`} />
      </div>
    );
  }

  return (
    <div
      data-testid={`${tipo}-by-date-page`}
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <Icon size={20} /> {cfg.title} par date
          </span>
          <div className="flex items-center gap-5 text-xs uppercase tracking-[0.2em]">
            <Link
              data-testid="list-other-link"
              to={cfg.otherTo}
              className="hidden text-[#F4F1EB]/70 transition-colors duration-300 hover:text-[#F4F1EB] sm:inline"
            >
              {cfg.otherLabel}
            </Link>
            <Link
              data-testid="list-dashboard-link"
              to="/admin"
              className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
            >
              Gestion
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-12 md:px-10">
        {items.length > 0 && (
          <div className="relative z-20 mb-10 flex flex-wrap items-center gap-3">
            <details data-testid="date-filter" className="group relative">
              <summary
                onClick={() => setSeenTick((t) => t + 1)}
                className="flex cursor-pointer list-none items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-[#102C1F] [&::-webkit-details-marker]:hidden"
              >
                <span className="relative inline-flex">
                  {selectedDate ? fmtMonth(selectedDate) : "Tous les mois"}
                  {totalUnseen > 0 && (
                    <span
                      data-testid="month-filter-dot"
                      className="pointer-events-none absolute -right-3 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 font-sans text-[10px] font-bold text-white shadow-md"
                    >
                      {totalUnseen}
                    </span>
                  )}
                </span>
                <ChevronDown
                  size={16}
                  className="transition-transform duration-300 group-open:rotate-180"
                />
              </summary>
              <div className="absolute left-0 mt-2 min-w-64 rounded-sm border border-[#102C1F]/15 bg-[#F4F1EB] py-2 shadow-[0_20px_40px_-20px_rgba(16,44,31,0.4)]">
                <button
                  data-testid="date-option-all"
                  onClick={(e) => {
                    setSelectedDate(null);
                    e.currentTarget.closest("details").open = false;
                  }}
                  className={`block w-full px-5 py-2.5 text-left text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    selectedDate === null
                      ? "bg-[#102C1F] text-[#F4F1EB]"
                      : "hover:bg-[#102C1F]/5"
                  }`}
                >
                  Tous les mois
                </button>
                {[...new Set(items.map((x) => cfg.dateOf(x).slice(0, 7)))]
                  .sort()
                  .reverse()
                  .map((m, i) => (
                    <button
                      key={m}
                      data-testid={`date-option-${i}`}
                      onClick={(e) => {
                        setSelectedDate(m);
                        e.currentTarget.closest("details").open = false;
                      }}
                      className={`flex w-full items-center justify-between px-5 py-2.5 text-left text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                        selectedDate === m
                          ? "bg-[#102C1F] text-[#F4F1EB]"
                          : "hover:bg-[#102C1F]/5"
                      }`}
                    >
                      {fmtMonth(m)}
                      {unseenByMonth[m] > 0 && (
                        <span
                          data-testid={`date-option-unseen-${i}`}
                          className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 font-sans text-[10px] font-bold text-white shadow-md"
                        >
                          {unseenByMonth[m]}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            </details>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#102C1F]/40"
              />
              <input
                data-testid="list-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, numéro ou date..."
                className="w-64 rounded-full border border-[#102C1F]/25 bg-[#F4F1EB] py-2.5 pl-10 pr-4 text-xs uppercase tracking-[0.15em] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/35 focus:border-[#102C1F]"
              />
            </div>
          </div>
        )}
        <DateGroups
          tipo={tipo}
          items={shownItems}
          onStatus={setStatus}
          onDelete={(id, name) => setToDelete({ tipo, id, name })}
          onPrinted={markPrinted}
          onEdit={tipo === "orders" ? setEditingOrder : undefined}
        />
      </main>

      {editingOrder && (
        <OrderEditModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={load}
        />
      )}

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
